import { useId } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { RegistrationOption, RegistrationType } from "@/types";

interface Values {
  registrationOptions: RegistrationOption[];
  registrationNotes: string;
}
interface Props extends Values {
  onChange: (patch: Partial<Values>) => void;
}
const types: { value: RegistrationType; label: string }[] = [
  { value: "phone", label: "Telefon" },
  { value: "email", label: "E-mail" },
  { value: "url", label: "Formularz / link" },
  { value: "ticket_url", label: "Link do biletu" },
  { value: "other", label: "Inne" },
];
export function RegistrationFields({ registrationOptions, registrationNotes, onChange }: Props) {
  const id = useId();
  const update = (index: number, patch: Partial<RegistrationOption>) =>
    onChange({
      registrationOptions: registrationOptions.map((option, i) =>
        i === index ? { ...option, ...patch } : option,
      ),
    });
  return (
    <div className="space-y-4">
      {registrationOptions.map((option, index) => (
        <fieldset key={index} className="space-y-3 rounded-lg border p-3">
          <legend className="px-1 text-sm font-medium">Sposób zapisu {index + 1}</legend>
          <Label htmlFor={`${id}-type-${index}`}>Typ zapisu {index + 1}</Label>
          <select
            id={`${id}-type-${index}`}
            className="w-full rounded-md border bg-background p-2 text-sm"
            value={option.type}
            onChange={(event) => update(index, { type: event.target.value as RegistrationType })}
          >
            {types.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <Label htmlFor={`${id}-value-${index}`}>Kontakt {index + 1}</Label>
          <Input
            id={`${id}-value-${index}`}
            value={option.value}
            maxLength={option.type === "other" ? 30000 : 2048}
            required
            onChange={(event) => update(index, { value: event.target.value })}
            placeholder={
              option.type === "phone"
                ? "+48 123 456 789"
                : option.type === "email"
                  ? "zapisy@example.com"
                  : option.type === "other"
                    ? "Informacja o zapisach"
                    : "https://..."
            }
          />
          <Label htmlFor={`${id}-label-${index}`}>Etykieta {index + 1} (opcjonalna)</Label>
          <Input
            id={`${id}-label-${index}`}
            value={option.label ?? ""}
            maxLength={100}
            onChange={(event) => update(index, { label: event.target.value || null })}
          />
          <Label htmlFor={`${id}-notes-${index}`}>Warunki {index + 1} (opcjonalne)</Label>
          <Textarea
            id={`${id}-notes-${index}`}
            value={option.notes ?? ""}
            maxLength={500}
            onChange={(event) => update(index, { notes: event.target.value || null })}
          />
          <Button
            type="button"
            variant="outline"
            aria-label={`Usuń sposób zapisu ${index + 1}`}
            onClick={() =>
              onChange({ registrationOptions: registrationOptions.filter((_, i) => i !== index) })
            }
          >
            Usuń sposób zapisu
          </Button>
        </fieldset>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={registrationOptions.length >= 10}
        onClick={() =>
          onChange({
            registrationOptions: [
              ...registrationOptions,
              { type: "phone", value: "", label: null, notes: null },
            ],
          })
        }
      >
        Dodaj sposób zapisu
      </Button>
      <div className="space-y-2">
        <Label htmlFor={`${id}-notes`}>Wspólne uwagi o zapisach</Label>
        <Textarea
          id={`${id}-notes`}
          value={registrationNotes}
          maxLength={2000}
          onChange={(event) => onChange({ registrationNotes: event.target.value })}
          placeholder="np. termin zapisów, limit miejsc"
        />
      </div>
    </div>
  );
}
